import React, { useEffect, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import AdbIcon from '@mui/icons-material/Adb';
import './Topbar.scss'
import Logo from '../../assets/images/logo.png'
import { FaAngleDown } from 'react-icons/fa';
import { FiChevronRight, FiLock, FiLogOut, FiShield, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../pages/Login/loginSlice';
import { useDispatch, useSelector } from 'react-redux';
import { decodeToken, useJwt } from "react-jwt";
import PaymentHistory from '../../assets/images/paymentHistory.png'
import WalletIcon from '../../assets/images/document-icon.png'
import { BsCurrencyRupee } from 'react-icons/bs';
import { getWalletBalance } from '../../pages/WalletDetails/walletSlice';
import { Snackbar, Alert, AlertTitle } from '@mui/material';
import { ENABLE_PRICING_AND_PAYMENTS } from '../../utils/featureFlags';
import {
  MONITORING_PULSE_DURATION_MS,
  subscribeToMonitoringPulse,
  createMonitoringResultObserver,
  publishOpenAlerts,
} from '../../utils/monitoringPulse';
import { formatCurrencyAmount } from '../../utils/taxUtils';
import api from '../../utils/api';
import { API_ENDPOINTS } from '../../utils/apiUrls';
import { isCustomerOnboardingRoute } from '../../utils/customerRoutes';

function TopBar() {
  const dispatch = useDispatch();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const userToken = localStorage.getItem('token');
  useEffect(() => {
    if (!userToken || isCustomerOnboardingRoute()) {
      // A customer's onboarding page has no staff alerts, and the gateway refuses the read.
      // Without a session there is nothing to alert on. Clearing it stops the red
      // Oomero Pulse state carrying over from the previous user after a sign-out.
      publishOpenAlerts(null);
      return undefined;
    }
    let active = true;
    let pending = false;
    const observe = createMonitoringResultObserver();
    const refresh = async () => {
      if (pending || document.hidden) return;
      pending = true;
      try {
        const response = await api.get('/dashboard/monitoringStatus');
        if (active) {
          observe(response.data?.data?.completed_at);
          publishOpenAlerts(response.data?.data?.open_alerts);
        }
      } catch (_) {
        // A failed status read must never signal a completed monitoring check.
      } finally { pending = false; }
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [userToken]);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [show, setShow] = useState(false);
  const accountMenuRef = useRef(null);
  // Dark mode switch is temporarily disabled.
  // const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // useEffect(() => {
  //   if (isDarkMode) {
  //     document.body.classList.add('dark-mode');
  //   } else {
  //     document.body.classList.remove('dark-mode');
  //   }
  //   localStorage.setItem('darkMode', isDarkMode);
  // }, [isDarkMode]);
  const navigate = useNavigate();
  const walletBalance = useSelector((state) => state.wallet?.walletBalance);
  const walletCurrencyCode = useSelector((state) => state.wallet?.currencyCode);
  const walletCurrencySymbol = useSelector((state) => state.wallet?.currencySymbol);
  const isBalanceLoaded = useSelector((state) => state.wallet?.isBalanceLoaded);
  const formattedWalletBalance = formatCurrencyAmount(
    walletBalance ?? 0,
    walletCurrencySymbol,
    walletCurrencyCode,
  );
  const [showBalanceAlert, setShowBalanceAlert] = useState(false);
  const [balanceAlertShown, setBalanceAlertShown] = useState(false);
  const LOW_WALLET_BALANCE_THRESHOLD = 25;

  const decodedToken = decodeToken(userToken);
  const displayName = [decodedToken?.firstName, decodedToken?.lastName].filter(Boolean).join(' ') || 'Account';
  const companyName = decodedToken?.companyName || decodedToken?.company_name || decodedToken?.entityName || decodedToken?.entity_name || '';
  const companyAccountReference = decodedToken?.companyAccountReference || decodedToken?.company_account_reference || '';
  const initials = [decodedToken?.firstName, decodedToken?.lastName]
    .filter(Boolean)
    .map((name) => name.charAt(0))
    .join('')
    .slice(0, 2) || 'U';
  const roleLabel = decodedToken?.isSuperAdmin
    ? 'Super Admin'
    : decodedToken?.isEnterpriseAdmin
      ? (companyName ? `Enterprise Admin · ${companyName}` : 'Enterprise Admin')
      : (decodedToken?.roles?.role_name || 'Team member');
  const planLabel = (decodedToken?.plan_type_id === 1 || decodedToken?.packageType === 'prepaid') ? 'Prepaid' : 'Postpaid';
  const pricingAndPaymentsEnabled = ENABLE_PRICING_AND_PAYMENTS;
  const isSuperAdmin = Boolean(decodedToken?.isSuperAdmin);
  const isEnterpriseAdmin = Boolean(decodedToken?.isEnterpriseAdmin);
  const hasOomeroBillingAccess = Boolean(
    decodedToken?.isOomero && decodedToken?.roles?.permissions?.view_client_billing_menu
  );
  const isClientWalletUser = Boolean(
    !decodedToken?.isOomero ||
    decodedToken?.packageType === 'prepaid' ||
    decodedToken?.packageType === 'postpaid'
  );
  const shouldCheckWalletBalance = pricingAndPaymentsEnabled && Boolean(decodedToken) && !isSuperAdmin && (
    isEnterpriseAdmin || decodedToken?.roles?.permissions?.view_wallet_balance === true
  );
  const walletError = useSelector((state) => state.wallet?.error);
  const isWalletPage = window.location.pathname === '/wallet-details';

  const routeInvoices = () => {
    let path = `/invoices`;
    navigate(path);
  }
  const routeGoogleAuthenticator = () => {
    let path = `/google_authenticator`;
    navigate(path);
  }
  const routeViewBilling = () => {
    let path = '/client_biling';
    navigate(path);
  }
  const routeBilling = () => {
    let path = '/billing';
    navigate(path);
  }
  const routePaymentHistory = () => {
    let path = `/payment_history`;
    navigate(path);
  }

  const routePermission = () => {
    let path = `/users/add_new_users`;
    navigate(path);
  }
  const routeWallet = () => {
    let path = '/wallet-details'
    navigate(path);
  }
  const routeChangePassword = () => {
    let path = `/change_password`;
    navigate(path);
  }
  const routeMyProfile = () => {
    let path = `/my_profile`;
    navigate(path);
  }
  const routeChangeLogout = async () => {
    let path = `/login`;
    try {
      await api.post(API_ENDPOINTS.logoutUser);
    } catch (error) {
      // Local logout must still happen even if the backend endpoint is unavailable.
    }
    dispatch(logout());
    navigate(path);
  }

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };


  const handleClose = () => setShow(false);

  useEffect(() => {
    if (!show) return undefined;

    const closeOnOutsideClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShow(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShow(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [show]);

  useEffect(() => {
    if (pricingAndPaymentsEnabled && ((!localStorage.getItem('activePlan') || localStorage.getItem('activePlan') == null || localStorage.getItem('activePlan') == 'false' || localStorage.getItem('activePlan') == 'undefined') && window.location.href.split('/')[3] !== 'pricing') && !decodedToken?.isOomero) {
      navigate('/pricing', { replace: true });
    }
  }, [decodedToken?.isOomero, navigate, pricingAndPaymentsEnabled])

  useEffect(() => {
    if (shouldCheckWalletBalance) {
      dispatch(getWalletBalance());
    }
  }, [dispatch, shouldCheckWalletBalance]);

  useEffect(() => {
    if (!shouldCheckWalletBalance) return undefined;
    const refreshWalletBalance = () => dispatch(getWalletBalance());
    window.addEventListener('oomero:billing-balance-updated', refreshWalletBalance);
    return () => window.removeEventListener('oomero:billing-balance-updated', refreshWalletBalance);
  }, [dispatch, shouldCheckWalletBalance]);

  // Show low/negative balance alert only after the live balance is loaded,
  // and only when it is below 25 (or negative).
  useEffect(() => {
    if (!shouldCheckWalletBalance || !isBalanceLoaded) {
      return;
    }

    const numericBalance = Number(walletBalance);
    const isNegative = Number.isFinite(numericBalance) && numericBalance < 0;
    const isLow = Number.isFinite(numericBalance) && numericBalance < LOW_WALLET_BALANCE_THRESHOLD;

    if (isNegative || isLow) {
      if (!balanceAlertShown) {
        setShowBalanceAlert(true);
        setBalanceAlertShown(true);
      }
      return;
    }

    setShowBalanceAlert(false);
  }, [
    walletBalance,
    balanceAlertShown,
    shouldCheckWalletBalance,
    isBalanceLoaded,
    LOW_WALLET_BALANCE_THRESHOLD,
  ]);

  // Heartbeat on the logo whenever continuous monitoring completes a pass.
  const [monitoringPulse, setMonitoringPulse] = useState(false);
  useEffect(() => {
    let timeoutId;
    const unsubscribe = subscribeToMonitoringPulse(() => {
      setMonitoringPulse(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setMonitoringPulse(false), MONITORING_PULSE_DURATION_MS);
    });
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  return (
    <div className='topBar'>
      <AppBar style={{ background: '#fff', position: 'fixed', minHeight: 58 }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters style={{ minHeight: 58, paddingTop: 0, paddingBottom: 0 }}>
            <button type="button" className="brand-button" onClick={() => navigate('/dashboard')} aria-label="Go to Oomero Pulse">
              <img
                className={`brand-button__logo${monitoringPulse ? ' brand-button__logo--pulsing' : ''}`}
                src={Logo}
                alt="Oomero"
              />
            </button>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            <AdbIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
            <Typography
              variant="h5"
              noWrap
              component="a"
              href=""
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              LOGO
            </Typography>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {shouldCheckWalletBalance && !isWalletPage && (
                <Box sx={{ ml: 'auto', mr: 2, display: 'flex', alignItems: 'center', bgcolor: '#f0f4ff', px: 1.5, py: 0.4, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#037AD0', fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    Wallet: <span style={{ color: walletError ? '#b42318' : '#198754' }}>{walletError ? 'Unavailable' : isBalanceLoaded ? formattedWalletBalance : 'Loading…'}</span>
                  </Typography>
                </Box>
              )}
            </Box>

            {companyAccountReference && (
              <div className="account-reference-banner" aria-label={`Oomero account number ${companyAccountReference}`}>
                <span>Oomero A/C No</span>
                <strong>{companyAccountReference}</strong>
              </div>
            )}

            <Box ref={accountMenuRef} sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', flexShrink: 0, position: 'relative' }}>
              <Tooltip title="Open account menu">
                <button
                  type="button"
                  className={`account-trigger ${show ? 'is-open' : ''}`}
                  onClick={() => setShow((prev) => !prev)}
                  aria-haspopup="menu"
                  aria-expanded={show}
                >
                  <Avatar className="account-trigger__avatar">{initials}</Avatar>
                  <span className="account-trigger__copy">
                    <strong>{displayName}</strong>
                    <small>{roleLabel}</small>
                  </span>
                  <FaAngleDown className="account-trigger__chevron" />
                </button>
              </Tooltip>

              {show && (
                <div className="account-popover" role="menu" aria-label="Account menu">
                  <div className="account-popover__header">
                    <div className="account-popover__avatar">{initials}</div>
                    <div>
                      <strong>{displayName}</strong>
                      <span>{decodedToken?.email || roleLabel}</span>
                      <div className="account-popover__badges">
                        <small>{roleLabel}</small>
                        <small>{planLabel}</small>
                      </div>
                    </div>
                  </div>

                  <div className="account-popover__section">
                    <span className="account-popover__label">Account</span>
                    <button type="button" role="menuitem" onClick={() => { routeMyProfile(); handleClose(); }}>
                      <i className="profile"><FiUser /></i>
                      <span><strong>My profile</strong><small>Personal and account details</small></span>
                      <FiChevronRight className="account-popover__arrow" />
                    </button>
                    <button type="button" role="menuitem" onClick={() => { routeChangePassword(); handleClose(); }}>
                      <i className="password"><FiLock /></i>
                      <span><strong>Change password</strong><small>Update your login password</small></span>
                      <FiChevronRight className="account-popover__arrow" />
                    </button>
                    <button type="button" role="menuitem" onClick={() => { routeGoogleAuthenticator(); handleClose(); }}>
                      <i className="security"><FiShield /></i>
                      <span><strong>Google Authenticator</strong><small>Manage two-step verification</small></span>
                      <FiChevronRight className="account-popover__arrow" />
                    </button>
                  </div>

                  <div className="account-popover__footer">
                    <button type="button" role="menuitem" onClick={routeChangeLogout}>
                      <FiLogOut />
                      <span>Log out</span>
                    </button>
                    <small>Oomero secure workspace</small>
                  </div>
                </div>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Low/Negative Balance Alert on Login */}
      <Snackbar
        open={showBalanceAlert}
        autoHideDuration={10000}
        onClose={() => setShowBalanceAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={walletBalance < 0 ? 'error' : 'warning'}
          onClose={() => setShowBalanceAlert(false)}
          sx={{ width: '100%', minWidth: '350px' }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {walletBalance < 0 ? 'Negative Wallet Balance' : 'Low Wallet Balance'}
          </AlertTitle>
          {walletBalance < 0
            ? `Your wallet balance is negative. Please top up immediately to continue using services.`
            : `Your wallet balance is low. Please top up to continue using services.`}
          <Box sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>
            Current Balance: {formattedWalletBalance}
          </Box>
        </Alert>
      </Snackbar>
    </div>
  );
}
export default TopBar;
